// Merkan · Barcode-Scanner: Hardware-Scanner (Tastatur-Emulation), Handy- oder Laptop-Kamera und die Suche nach dem gescannten Code.
// Ein Hardware-Scanner tippt den Code sehr schnell und schickt Enter. Steht der Fokus in keinem Eingabefeld, fängt Merkan die
// Tastenfolge ab und öffnet das Scanfenster. In einem Eingabefeld (zum Beispiel EAN im Artikel) landet der Code ganz normal im Feld.
// Kamera: BarcodeDetector des Browsers (Chrome, Edge, Android), sonst ZXing als Ersatz (Safari, Firefox), erst bei Bedarf geladen.
import { supabase } from './supabase';
import { gtinNormal, istGtin } from './gtin';

// ---------------------------------------------------------------------------------------------------------------
// Hardware-Scanner

const MAX_ABSTAND_MS = 45;   // Scanner: wenige Millisekunden je Zeichen; Menschen tippen deutlich langsamer
const MIN_LAENGE = 6;

function inEingabe(el: EventTarget | null): boolean {
  const e = el as HTMLElement | null;
  if (!e) return false;
  if (e.isContentEditable) return true;
  const tag = e.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const typ = (e as HTMLInputElement).type;
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(typ);
  }
  return false;
}

/** Lauscht global auf Hardware-Scanner; gibt eine Abmeldefunktion zurück */
export function handscannerLauschen(gefunden: (code: string) => void): () => void {
  let puffer = '';
  let letzte = 0;
  let schnell = true;
  const taste = (ev: KeyboardEvent) => {
    if (ev.ctrlKey || ev.altKey || ev.metaKey || inEingabe(ev.target)) { puffer = ''; return; }
    const jetzt = performance.now();
    const abstand = jetzt - letzte;
    letzte = jetzt;
    if (abstand > MAX_ABSTAND_MS * 4) { puffer = ''; schnell = true; }
    if (ev.key === 'Enter' || ev.key === 'Tab') {
      if (puffer.length >= MIN_LAENGE && schnell) {
        ev.preventDefault();
        ev.stopPropagation();
        gefunden(puffer);
      }
      puffer = '';
      schnell = true;
      return;
    }
    if (ev.key.length !== 1) return;
    if (puffer && abstand > MAX_ABSTAND_MS) schnell = false;
    puffer += ev.key;
  };
  window.addEventListener('keydown', taste, true);
  return () => window.removeEventListener('keydown', taste, true);
}

// ---------------------------------------------------------------------------------------------------------------
// Kamera

interface Erkenner { detect(quelle: CanvasImageSource): Promise<{ rawValue: string }[]> }
type ErkennerKlasse = { new (o: { formats: string[] }): Erkenner; getSupportedFormats(): Promise<string[]> };
const FORMATE = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'qr_code', 'data_matrix', 'itf'];

export const kameraVorhanden = () => typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);

/** Startet die Kamera im Video-Element und meldet jeden erkannten Code; gibt eine Stoppfunktion zurück */
export async function kameraStarten(video: HTMLVideoElement, gefunden: (code: string) => void): Promise<() => void> {
  const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
  let aus = false;
  const stoppen: (() => void)[] = [() => stream.getTracks().forEach((t) => t.stop())];
  video.srcObject = stream;
  video.setAttribute('playsinline', 'true');
  video.muted = true;
  await video.play().catch(() => undefined);

  const Klasse = (window as unknown as { BarcodeDetector?: ErkennerKlasse }).BarcodeDetector;
  const unterstuetzt = Klasse ? await Klasse.getSupportedFormats().catch(() => [] as string[]) : [];
  const formate = FORMATE.filter((f) => unterstuetzt.includes(f));
  if (Klasse && formate.length) {
    const erkenner = new Klasse({ formats: formate });
    let laeuft = false;
    const takt = window.setInterval(async () => {
      if (aus || laeuft || video.readyState < 2) return;
      laeuft = true;
      try {
        const r = await erkenner.detect(video);
        if (r[0]?.rawValue && !aus) gefunden(r[0].rawValue);
      } catch { /* einzelnes Bild nicht lesbar */ }
      laeuft = false;
    }, 180);
    stoppen.push(() => window.clearInterval(takt));
  } else {
    const { BrowserMultiFormatReader } = await import('@zxing/browser');
    const leser = new BrowserMultiFormatReader();
    const steuerung = await leser.decodeFromStream(stream, video, (ergebnis) => { if (ergebnis && !aus) gefunden(ergebnis.getText()); });
    stoppen.push(() => steuerung.stop());
  }
  return () => { aus = true; stoppen.forEach((f) => { try { f(); } catch { /* bereits gestoppt */ } }); video.srcObject = null; };
}

// ---------------------------------------------------------------------------------------------------------------
// Suche

export interface ScanArtikel { id: string; artikelnummer: string; bezeichnung: string; hersteller: string | null; herstellernummer: string | null; ean: string | null; einheit: string; treffer: 'ean' | 'artikelnummer' | 'teilenummer' }
export interface ScanPosition {
  id: string; vorgang_id: string; vorgang_nummer: string | null; kunde: string | null; phase: string; pos: number; artikel_id: string | null;
  menge: number; eingang_menge: number; einheit: string;
  bestellung: { id: string; nummer: string | null; status: string; lieferant: string | null; liefertermin: string | null } | null;
}
export interface ScanTreffer {
  code: string;
  artikel: ScanArtikel[];
  positionen: ScanPosition[];
  bestellungen: { id: string; vorgang_id: string; nummer: string | null; status: string }[];
  vorgaenge: { id: string; nummer: string | null; phase: string }[];
}

const wie = (s: string) => s.replace(/[\\%_]/g, (z) => `\\${z}`);
const zahl = (v: unknown) => Number(v ?? 0) || 0;

export async function scanSuchen(mandantId: string, roh: string): Promise<ScanTreffer> {
  const code = roh.trim();
  const g = gtinNormal(code);
  const muster = wie(code);
  const [ean, nr, teil, best, vg] = await Promise.all([
    istGtin(g) ? supabase.from('artikel').select('id, artikelnummer, bezeichnung, hersteller, herstellernummer, ean, einheit').eq('mandant_id', mandantId).eq('ean', g).limit(5) : Promise.resolve({ data: [] }),
    supabase.from('artikel').select('id, artikelnummer, bezeichnung, hersteller, herstellernummer, ean, einheit').eq('mandant_id', mandantId).ilike('artikelnummer', muster).limit(5),
    supabase.from('artikel').select('id, artikelnummer, bezeichnung, hersteller, herstellernummer, ean, einheit').eq('mandant_id', mandantId).ilike('herstellernummer', muster).limit(5),
    supabase.from('vorgang_bestellungen').select('id, vorgang_id, nummer, status').eq('mandant_id', mandantId).ilike('nummer', muster).limit(5),
    supabase.from('vorgaenge').select('id, nummer, phase').eq('mandant_id', mandantId).ilike('nummer', muster).limit(5),
  ]);
  type A = Omit<ScanArtikel, 'treffer'>;
  const artikel = new Map<string, ScanArtikel>();
  for (const x of (ean.data ?? []) as A[]) artikel.set(x.id, { ...x, treffer: 'ean' });
  for (const x of (nr.data ?? []) as A[]) if (!artikel.has(x.id)) artikel.set(x.id, { ...x, treffer: 'artikelnummer' });
  for (const x of (teil.data ?? []) as A[]) if (!artikel.has(x.id)) artikel.set(x.id, { ...x, treffer: 'teilenummer' });

  // Offene Vorgänge mit diesem Artikel: über die Artikelverknüpfung, die gleiche Teilenummer oder den gescannten Code als Teilenummer
  // (viele Positionen aus Anfragen sind mit keinem Artikelstamm verknüpft und tragen nur die Teilenummer)
  const ids = [...artikel.keys()];
  const teilenummern = [...new Set([...artikel.values()].map((a) => a.herstellernummer).filter((x): x is string => Boolean(x)))];
  let positionen: ScanPosition[] = [];
  {
    const auswahl = 'id, vorgang_id, pos, artikel_id, herstellernummer, menge, eingang_menge, einheit';
    const [pa, pt, pc] = await Promise.all([
      ids.length ? supabase.from('vorgang_positionen').select(auswahl).eq('mandant_id', mandantId).eq('gestrichen', false).in('artikel_id', ids).limit(200) : Promise.resolve({ data: [] }),
      teilenummern.length ? supabase.from('vorgang_positionen').select(auswahl).eq('mandant_id', mandantId).eq('gestrichen', false).is('artikel_id', null).in('herstellernummer', teilenummern).limit(200) : Promise.resolve({ data: [] }),
      code.length >= 4 ? supabase.from('vorgang_positionen').select(auswahl).eq('mandant_id', mandantId).eq('gestrichen', false).ilike('herstellernummer', muster).limit(200) : Promise.resolve({ data: [] }),
    ]);
    type P = { id: string; vorgang_id: string; pos: number; artikel_id: string | null; herstellernummer: string | null; menge: number | string; eingang_menge: number | string; einheit: string };
    const roh = [...new Map([...((pa.data ?? []) as P[]), ...((pt.data ?? []) as P[]), ...((pc.data ?? []) as P[])].map((x) => [x.id, x])).values()];
    const vids = [...new Set(roh.map((p) => p.vorgang_id))];
    if (vids.length) {
      const [v, b] = await Promise.all([
        supabase.from('vorgaenge').select('id, nummer, phase, kunde_id, abgeschlossen_am').eq('mandant_id', mandantId).in('id', vids).is('abgeschlossen_am', null),
        supabase.from('vorgang_bestellungen').select('id, vorgang_id, nummer, status, positionen, lieferant_id, liefertermin').eq('mandant_id', mandantId).in('vorgang_id', vids).neq('status', 'storniert'),
      ]);
      type V = { id: string; nummer: string | null; phase: string; kunde_id: string | null };
      type B = { id: string; vorgang_id: string; nummer: string | null; status: string; positionen: string[]; lieferant_id: string; liefertermin: string | null };
      const vorg = new Map(((v.data ?? []) as V[]).map((x) => [x.id, x]));
      const bests = (b.data ?? []) as B[];
      const kids = [...new Set([...vorg.values()].map((x) => x.kunde_id).filter((x): x is string => Boolean(x)))];
      const lids = [...new Set(bests.map((x) => x.lieferant_id))];
      const [k, l] = await Promise.all([
        kids.length ? supabase.from('kunden').select('id, firma').eq('mandant_id', mandantId).in('id', kids) : Promise.resolve({ data: [] }),
        lids.length ? supabase.from('lieferanten').select('id, firma, kurzname').eq('mandant_id', mandantId).in('id', lids) : Promise.resolve({ data: [] }),
      ]);
      const kunde = new Map(((k.data ?? []) as { id: string; firma: string }[]).map((x) => [x.id, x.firma]));
      const lief = new Map(((l.data ?? []) as { id: string; firma: string; kurzname: string | null }[]).map((x) => [x.id, x.kurzname || x.firma]));
      positionen = roh.filter((p) => vorg.has(p.vorgang_id)).map((p) => {
        const vv = vorg.get(p.vorgang_id)!;
        const bb = bests.find((x) => x.positionen.includes(p.id));
        return {
          id: p.id, vorgang_id: p.vorgang_id, vorgang_nummer: vv.nummer, kunde: vv.kunde_id ? kunde.get(vv.kunde_id) ?? null : null, phase: vv.phase, pos: p.pos,
          artikel_id: p.artikel_id, menge: zahl(p.menge), eingang_menge: zahl(p.eingang_menge), einheit: p.einheit,
          bestellung: bb ? { id: bb.id, nummer: bb.nummer, status: bb.status, lieferant: lief.get(bb.lieferant_id) ?? null, liefertermin: bb.liefertermin } : null,
        };
      }).sort((a, c) => Number(Boolean(c.bestellung && c.eingang_menge < c.menge)) - Number(Boolean(a.bestellung && a.eingang_menge < a.menge)) || (a.vorgang_nummer ?? '').localeCompare(c.vorgang_nummer ?? ''));
    }
  }
  return {
    code,
    artikel: [...artikel.values()],
    positionen,
    bestellungen: (best.data ?? []) as ScanTreffer['bestellungen'],
    vorgaenge: (vg.data ?? []) as ScanTreffer['vorgaenge'],
  };
}

/** Einem Artikel den gescannten Barcode zuordnen */
export async function eanZuordnen(mandantId: string, artikelId: string, code: string) {
  return supabase.from('artikel').update({ ean: gtinNormal(code) }).eq('mandant_id', mandantId).eq('id', artikelId);
}
